import { ComponentHeader } from '../shared/ComponentHeader'
import { ComponentTitle } from '../shared/ComponentTitle'
import clsx from 'clsx'
import { RadialChartDataSchema, RadialChartUISchema } from './types'
import { RadialBarChart, RadialBar, PolarGrid, PolarAngleAxis } from 'recharts'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegendContent,
  ChartConfig as ShadcnChartConfig,
  // ChartStyle,
} from '@/components/shadcn/charts'
import { DynamicIcon } from '@/components/icons/DynamicIcon'
import { ComponentActionsContainer } from '../shared/ComponentActionsContainer'
import { normalizeRechartsData } from '../shared/rechartsDataUtils'
import { getComponentHeights } from '../utils'

export function ChartRadialContent({
  uiSchema,
  dataSchema,
  tileFilters,
}: {
  uiSchema: RadialChartUISchema
  dataSchema: RadialChartDataSchema
  tileFilters?: React.ReactNode
}) {
  const { title = undefined, description = undefined, chartConfig, emphasizeHeader } = uiSchema
  const hasHeader = !!title && !!tileFilters
  const { contentHeight } = getComponentHeights(emphasizeHeader, hasHeader)
  const { config, legend, tooltip, barDataKey, isPercentageScale } = chartConfig
  const configWithIcon: ShadcnChartConfig = Object.fromEntries(
    Object.entries(config).map(([key, value]) => {
      const { icon: iconName, ...rest } = value
      return [key, iconName ? { ...rest, icon: () => <DynamicIcon iconName={iconName} lazyLoad={false} /> } : rest]
    })
  )
  const { data: rawData } = dataSchema
  const { data, mapDataKey } = normalizeRechartsData(rawData, [legend.nameKey, tooltip.nameKey, barDataKey])
  const normalizedLegendNameKey = mapDataKey(legend.nameKey)
  const normalizedTooltipNameKey = mapDataKey(tooltip.nameKey)
  const normalizedBarDataKey = mapDataKey(barDataKey)
  const normalizedLegend = { ...legend, nameKey: normalizedLegendNameKey }
  const normalizedTooltip = { ...tooltip, nameKey: normalizedTooltipNameKey }
  const dataWithFill = data.map((item) => {
    const categoryValue = item[normalizedLegendNameKey]
    return {
      ...item,
      fill: configWithIcon[categoryValue as keyof typeof configWithIcon]?.color,
    }
  })
  const legendItems = Array.from(new Set(data.map((item) => String(item[normalizedLegendNameKey])))).map((key) => ({
    key,
    label: configWithIcon[key]?.label ?? key,
    color: configWithIcon[key]?.color,
    icon: configWithIcon[key]?.icon,
  }))
  const domainMax = Math.max(100, ...data.map((item) => Number(item[normalizedBarDataKey] ?? 0)))

  const polarAngleAxis = isPercentageScale ? (
    <PolarAngleAxis type="number" domain={[0, domainMax]} tick={false} />
  ) : null

  return (
    <div className="flex h-full flex-col">
      {hasHeader && (
        <ComponentHeader emphasize={emphasizeHeader}>
          <ComponentTitle title={title} description={description} />
          <ComponentActionsContainer>{tileFilters}</ComponentActionsContainer>
        </ComponentHeader>
      )}
      <div className={clsx({ 'pt-5': hasHeader })} style={{ height: contentHeight }}>
        <ChartContainer
          config={configWithIcon}
          className="mx-auto aspect-square max-h-full"
          legend={<ChartLegendContent {...normalizedLegend} items={legendItems} />}
        >
          {/* <RadialBarChart data={dataWithFill} innerRadius="30%" startAngle={90} endAngle={-270} barCategoryGap="18%"> */}
          <RadialBarChart data={dataWithFill} innerRadius="30%" barCategoryGap="18%" startAngle={90} endAngle={-270}>
            <ChartTooltip content={<ChartTooltipContent {...normalizedTooltip} /* hideLabel={false} */ />} />
            <PolarGrid gridType="circle" polarAngles={[90]} />
            {polarAngleAxis}
            <RadialBar dataKey={normalizedBarDataKey} background cornerRadius="50%" />
          </RadialBarChart>
        </ChartContainer>
      </div>
    </div>
  )
}
